import { BrandMark } from './BrandMark';
import { collections, featuredQuestions, homeForLanguage, pageForKnowledgeId } from './data';

import { ArrowIcon, ArrowUpRightIcon, SearchIcon } from './icons';
import { JourneySelector } from './JourneySelector';
import { PageHeaderImage } from './PageHeaderImage';
import { Link } from './router';
import type { SiteLanguage, SitePageSummary } from './types';
import './reader-design.css';
import './home-identity.css';

const copy = {
  en: {
    eyebrow: 'Community-built surgical knowledge',
    title: 'bottomsurgery.info',
    intro: 'Clear, patient-centered information for exploring procedures, preparing for surgery, caring for yourself afterward, and finding the next useful question.',

    explore: 'Where to begin',
    search: 'Search the full site',
    procedures: 'Procedure guides',
    proceduresTitle: 'Start with the care you are considering.',
    proceduresBody: 'Long-form guides on the procedures represented in this library, with practical context and links outward when a decision needs more than one page.',
    allProcedures: 'Compare procedures',
    questions: 'Find an answer, then keep going',
    questionsTitle: 'Questions people bring here.',
    topics: 'More ways to enter',
    topicsTitle: 'Preparation, recovery, hormones, support, and stories.',

    stories: 'Lived experience',
    storiesTitle: 'Experience belongs beside the information.',
    storiesBody: 'Personal stories, family knowledge, and community conversations are substantive knowledge with a different scope from clinical guidance—not a lower rung, and never a stand-in for anyone else’s path.',
    storiesLink: 'Read lived experiences',
    bookEyebrow: 'The complete guide',
    bookTitle: 'Becoming Yourself',
    bookBody: 'A comprehensive companion for readers who want the full roadmap in one place. The website remains free; the guide supports the work and brings its material together offline.',
    bookLink: 'Open the complete guide',
    bookMeta: 'Gender affirming surgery · 1,093 pages',
    boundary: 'This is community education, not individualized medical advice. Bring questions from here to qualified clinicians who know your care.',
    sourceNote: 'Original artwork from bottomsurgery.info',
  },
  es: {
    eyebrow: 'Conocimiento quirúrgico creado por la comunidad',
    title: 'bottomsurgery.info',
    intro: 'Información clara y centrada en pacientes para explorar procedimientos, prepararte para la cirugía, cuidarte después y encontrar la siguiente pregunta útil.',

    explore: 'Por dónde empezar',
    search: 'Buscar en todo el sitio',
    procedures: 'Guías de procedimientos',
    proceduresTitle: 'Empieza con la atención que estás considerando.',
    proceduresBody: 'Guías extensas sobre los procedimientos de esta biblioteca, con contexto práctico y enlaces cuando una decisión necesita más de una página.',
    allProcedures: 'Comparar procedimientos',
    questions: 'Encuentra una respuesta y sigue adelante',
    questionsTitle: 'Preguntas que las personas traen aquí.',
    topics: 'Más formas de entrar',
    topicsTitle: 'Preparación, recuperación, hormonas, apoyo e historias.',

    stories: 'Experiencia vivida',
    storiesTitle: 'La experiencia está junto a la información.',
    storiesBody: 'Las historias personales, el conocimiento familiar y las conversaciones comunitarias son conocimiento sustantivo con un alcance distinto de la guía clínica; no son un nivel inferior ni sustituyen el camino de otra persona.',
    storiesLink: 'Leer experiencias vividas',
    bookEyebrow: 'La guía completa',
    bookTitle: 'Becoming Yourself',
    bookBody: 'Un acompañante integral para quien quiere el mapa completo en un solo lugar. El sitio sigue siendo gratuito; la guía apoya el trabajo y reúne el material para leer sin conexión.',
    bookLink: 'Abrir la guía completa',
    bookMeta: 'Cirugía de afirmación de género · 1.093 páginas',
    boundary: 'Este es un recurso educativo comunitario, no asesoramiento médico individual. Lleva las preguntas de aquí a profesionales calificados que conozcan tu atención.',
    sourceNote: 'Obra original de bottomsurgery.info',
  },
};

function pageFor(language: SiteLanguage, knowledgeId: string, pages: SitePageSummary[]) {
  return pages.find((page) => page.lang === language && page.knowledgeId === knowledgeId && page.wordCount > 0) ?? null;
}

function routeFor(language: SiteLanguage, knowledgeId: string, pages: SitePageSummary[], fallback: string) {
  return pageFor(language, knowledgeId, pages)?.route ?? pageForKnowledgeId(language, knowledgeId)?.route ?? fallback;
}

export function HomePage({ language, pages, searchOpen = false, onSearch }: { language: SiteLanguage; pages: SitePageSummary[]; searchOpen?: boolean; onSearch?: () => void }) {
  const lang = language === 'es' ? 'es' : 'en';
  const c = copy[lang];
  const articles = pages.filter((page) => page.lang === lang && page.wordCount > 0);
  const procedures = articles.filter((page) => page.kind === 'procedure');
  const stories = articles.filter((page) => page.kind === 'story');
  const comparison = routeFor(lang, 'compare', pages, lang === 'es' ? '/es/comparar' : '/compare');
  const book = routeFor(lang, 'book', pages, '/book');
  const storyCollection = routeFor(lang, 'collection-stories', pages, lang === 'es' ? '/es/historias' : '/stories');


  return (
    <main id="main-content" className="identity-home">
      <section className="identity-hero">
        <div className="shell identity-hero__inner">
          <PageHeaderImage page={homeForLanguage(lang)} priority className="identity-hero__art" />
          <div className="identity-hero__copy">
            <span className="identity-kicker">{c.eyebrow}</span>
            <h1>{c.title}</h1>
            <p>{c.intro}</p>
            <div className="identity-hero__actions">
              <Link className="identity-button identity-button--primary" href="#journey-selector-title">{c.explore}<ArrowIcon /></Link>
              <button className="identity-search" type="button" onClick={onSearch} aria-haspopup="dialog" aria-expanded={searchOpen} aria-controls="site-search-dialog"><SearchIcon />{c.search}</button>
            </div>
          </div>
        </div>
      </section>

      <JourneySelector language={lang} />

      <section className="identity-procedures shell" aria-labelledby="procedure-title">
        <header className="identity-section-heading">
          <span>{c.procedures}</span>
          <div><h2 id="procedure-title">{c.proceduresTitle}</h2><p>{c.proceduresBody}</p></div>
          <Link href={comparison}>{c.allProcedures}<ArrowIcon /></Link>
        </header>
        <nav className="procedure-orbit" aria-label={c.procedures}>
          {procedures.map((procedure) => (
            <Link key={procedure.id} className="procedure-orbit__item" href={procedure.route}>
              <span>{procedure.readMinutes} {lang === 'es' ? 'min' : 'min read'}</span>
              <strong>{procedure.title}</strong>
              <ArrowUpRightIcon />
            </Link>
          ))}
        </nav>
      </section>

      <section className="identity-questions" aria-labelledby="question-title">
        <div className="shell identity-questions__inner">
          <header><span>{c.questions}</span><h2 id="question-title">{c.questionsTitle}</h2></header>
          <ol>
            {featuredQuestions.map((question) => {
              const target = pageFor(lang, question.pageId, pages);
              return target ? <li key={question.pageId}><Link href={target.route}><span>↗</span>{question[lang]}<ArrowIcon /></Link></li> : null;
            })}
          </ol>
        </div>
      </section>

      <section className="identity-topics shell" aria-labelledby="topic-title">
        <header className="identity-section-heading">
          <span>{c.topics}</span>
          <div><h2 id="topic-title">{c.topicsTitle}</h2></div>
        </header>
        <nav className="identity-topic-rows" aria-label={c.topics}>
          {collections.map((collection) => {
            const target = pageForKnowledgeId(lang, `collection-${collection.id}`)?.route ?? collection.route[lang];
            return (
              <Link href={target} key={collection.id} className="identity-topic-row">
                <div><h3>{collection.label[lang]}</h3><p>{collection.summary[lang]}</p></div>
                <ArrowIcon />
              </Link>
            );
          })}
        </nav>
      </section>

      <section className="identity-story">
        <div className="shell identity-story__inner">
          <div className="identity-story__lotus" aria-hidden="true"><BrandMark /></div>
          <div><span>{c.stories}</span><h2>{c.storiesTitle}</h2></div>
          <div><p>{c.storiesBody}</p><Link href={storyCollection}>{c.storiesLink}<ArrowIcon /></Link></div>
          <div className="identity-story__links" aria-label={c.stories}>
            {stories.map((story) => <Link key={story.id} href={story.route}>{story.title}<ArrowUpRightIcon /></Link>)}
          </div>
        </div>
      </section>

      <section className="identity-book shell" aria-labelledby="book-title">
        <div className="identity-book__cover"><img src="/brand/becoming-yourself-source.jpg" alt="Becoming Yourself: The Complete Guide to Gender Affirming Surgery" /><small>{c.sourceNote}</small></div>
        <div className="identity-book__copy"><span>{c.bookEyebrow}</span><h2 id="book-title">{c.bookTitle}</h2><p>{c.bookBody}</p><small>{c.bookMeta}</small><Link href={book}>{c.bookLink}<ArrowIcon /></Link></div>
        <div className="identity-book__traces" aria-hidden="true"><BrandMark /></div>
      </section>

      <section className="identity-boundary shell"><span>INFORMATION, NOT A PRESCRIPTION</span><p>{c.boundary}</p></section>
    </main>
  );
}
